import { useState, useRef, useEffect } from "react";
import { CalendarDays, ChevronLeft, ChevronRight } from "lucide-react";
import { useTranslation } from "react-i18next";
import { formatToShortMonth } from "@/lib/time_utils";

const years = Array.from(
  { length: 10 },
  (_, i) => new Date().getFullYear() - i
);

export default function MonthYearSelector({
  selectedMonths = [],
  selectedYear = new Date().getFullYear(),
  onChange,
}: {
  selectedMonths?: number[];
  selectedYear?: number;
  onChange: (value: { months: number[]; year: number }) => void;
}) {
  const { t, i18n } = useTranslation("common");
  const locale = i18n.language;
  const currentMonthIndex = new Date().getMonth();
  const currentYear = new Date().getFullYear();

  const months = Array.from({ length: 12 }, (_, i) =>
    formatToShortMonth(
      new Intl.DateTimeFormat(locale, { month: "short" }).format(
        new Date(2000, i, 1)
      )
    )
  );
  const initialMonthName =
    selectedMonths.length != 12
      ? months[selectedMonths[0] - 1]
      : months[currentMonthIndex];

  const [open, setOpen] = useState(false);
  const [monthsState, setMonthsState] = useState<string[]>([initialMonthName]);
  const [yearState, setYearState] = useState(selectedYear || currentYear);

  const [tempMonthsState, setTempMonthsState] = useState<string[]>([
    initialMonthName,
  ]);
  const [tempYearState, setTempYearState] = useState(
    selectedYear || currentYear
  );

  const ref = useRef<HTMLDivElement>(null);
  const minYear = Math.min(...years);
  const maxYear = Math.max(...years);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        applyChangesAndClose();
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [tempMonthsState, tempYearState]);

  const applyChangesAndClose = () => {
    setOpen(false);
    setMonthsState(tempMonthsState);
    setYearState(tempYearState);
    const numericMonths = tempMonthsState.map((m) => months.indexOf(m) + 1);
    onChange({ months: numericMonths, year: tempYearState });
  };

  const toggleMonth = (month: string) => {
    setTempMonthsState([month]);
  };

  const displayLabel =
    monthsState.length > 0
      ? `${monthsState[0]} | ${yearState}`
      : `${months[currentMonthIndex]} | ${yearState}`;

  return (
    <div className="relative inline-block text-left" ref={ref}>
      <div className="inline-flex items-center bg-gray-50 border border-gray-200 rounded-xl text-sm text-gray-600 hover:text-black">
        <button
          onClick={(e) => {
            e.stopPropagation();
            if (yearState > minYear) {
              const newY = yearState - 1;
              setYearState(newY);
              setTempYearState(newY);
              onChange({
                months: monthsState.map((m) => months.indexOf(m) + 1),
                year: newY,
              });
            }
          }}
          disabled={yearState <= minYear}
          className={`px-2 py-1 hover:bg-gray-100 rounded-l-xl ${
            yearState <= minYear ? "opacity-30 cursor-not-allowed" : ""
          }`}
        >
          <ChevronLeft className="w-4 h-4" />
        </button>

        <div
          onClick={() => {
            if (!open) {
              setTempMonthsState(monthsState);
              setTempYearState(yearState);
              setOpen(true);
            } else {
              applyChangesAndClose();
            }
          }}
          className="flex items-center gap-2 px-1 py-1.5 cursor-pointer"
        >
          <CalendarDays className="w-4 h-4" />
          {displayLabel}
        </div>

        <button
          onClick={(e) => {
            e.stopPropagation();
            if (yearState < maxYear) {
              const newY = yearState + 1;
              setYearState(newY);
              setTempYearState(newY);
              onChange({
                months: monthsState.map((m) => months.indexOf(m) + 1),
                year: newY,
              });
            }
          }}
          disabled={yearState >= maxYear}
          className={`px-2 py-1 hover:bg-gray-100 rounded-r-xl ${
            yearState >= maxYear ? "opacity-30 cursor-not-allowed" : ""
          }`}
        >
          <ChevronRight className="w-4 h-4" />
        </button>
      </div>

      {open && (
        <div className="absolute right-0 z-10 mt-2 w-64 bg-white border border-gray-200 rounded-xl shadow-xl p-3 space-y-3">
          <div>
            <label className="block text-sm font-medium mb-1">
              {t("year")}
            </label>
            <select
              value={tempYearState}
              onChange={(e) => setTempYearState(Number(e.target.value))}
              className="w-full border rounded px-2 py-1 text-sm"
            >
              {years.map((y) => (
                <option key={y}>{y}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">
              {t("months")}
            </label>
            <div className="grid grid-cols-4 gap-1">
              {months.map((m) => (
                <button
                  key={m}
                  onClick={() => toggleMonth(m)}
                  className={`text-xs px-2 py-1 rounded ${
                    tempMonthsState.includes(m)
                      ? "bg-blue-600 text-white"
                      : "bg-gray-100 hover:bg-gray-200"
                  }`}
                >
                  {m}
                </button>
              ))}
            </div>

            <div className="mt-3 flex justify-between gap-2 py-0-5">
              <button
                onClick={() => setTempMonthsState([])}
                className="w-1/2 flex items-center justify-center gap-2 text-sm text-gray-800 bg-gray-50 border border-gray-300 hover:bg-gray-100 shadow-sm rounded-xl px-3 py-1.5 transition"
              >
                {t("clear")}
              </button>

              <button
                onClick={applyChangesAndClose}
                className="w-1/2 flex items-center justify-center gap-2 text-sm text-gray-800 bg-gray-50 border border-gray-300 hover:bg-gray-100 shadow-sm rounded-xl px-3 py-1.5 transition"
              >
                {t("confirm")}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
